import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { 
  HiHome, 
  HiOfficeBuilding, 
  HiLocationMarker,
  HiUserGroup,
  HiCube,
  HiShoppingCart,
  HiCurrencyDollar,
  HiCash,
  HiDocumentText,
  HiClipboardList,
  HiCreditCard,
  HiTrash,
  HiChevronDown,
  HiChevronRight,
  HiSwitchHorizontal
} from 'react-icons/hi';
import Tooltip from './Tooltip';
import './Sidebar.css';

const Sidebar = ({ open = false, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { currentBranch } = useBranch();
  const isMainBranch = currentBranch && (currentBranch.is_main === true || currentBranch.isMain === true);
  const [branches, setBranches] = useState([]);
  const [deletedDataOpen, setDeletedDataOpen] = useState(false);

  useEffect(() => {
    if (['/deleted/parties', '/deleted/products', '/deleted/work-orders'].includes(location.pathname)) {
      setDeletedDataOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      axios.get(`${API}/branches`)
        .then((res) => {
          let branchData = [];
          if (res.data) {
            if (Array.isArray(res.data.branches)) {
              branchData = res.data.branches;
            } else if (res.data.data && Array.isArray(res.data.data.branches)) {
              branchData = res.data.data.branches;
            } else if (Array.isArray(res.data)) {
              branchData = res.data;
            }
          }
          setBranches(Array.isArray(branchData) ? branchData : []);
        })
        .catch(() => setBranches([]));
    }
  }, [user?.role]);

  const isActive = (path) => location.pathname === path;
  const isDeletedDataActive = ['/deleted/parties', '/deleted/products', '/deleted/work-orders'].some(p => location.pathname === p);

  const getSidebarTitle = () => {
    if (user?.role === 'super_admin') return 'Hello Boss 😎';
    if (user?.business?.name) return user.business.name;
    return 'POS System';
  };

  const handleNavClick = () => {
    if (typeof onClose === 'function') onClose();
  };

  const publicPath = process.env.PUBLIC_URL || '';
  const logoUrl = `${publicPath}/assets/safari-club-no-background.png`;

  return (
    <div className={`sidebar ${open ? 'open' : ''}`}>
      <div className="sidebar-header">
        <Link to="/dashboard" className="sidebar-brand" onClick={handleNavClick}>
          <img
            src={logoUrl}
            alt="Logo"
            className="sidebar-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="sidebar-business-name">{getSidebarTitle()}</span>
        </Link>
      </div>
      <nav className="sidebar-nav">
        <Tooltip text="View dashboard and statistics" position="right">
          <Link to="/dashboard" className={isActive('/dashboard') ? 'active' : ''} onClick={handleNavClick}>
            <HiHome className="nav-icon" />
            <span>Dashboard</span>
          </Link>
        </Tooltip>
        
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <Tooltip text="Manage branches" position="right">
            <Link to="/branches" className={isActive('/branches') ? 'active' : ''} onClick={handleNavClick}>
              <HiLocationMarker className="nav-icon" />
              <span>Branches</span>
            </Link>
          </Tooltip>
        )}

        {/* Inventory Section */}
        <div className="sidebar-section">
          <div className="section-title">Inventory</div>
          <Tooltip text="Manage vendors, customers, tailors" position="right">
            <Link to="/parties" className={isActive('/parties') ? 'active' : ''} onClick={handleNavClick}>
              <HiUserGroup className="nav-icon" />
              <span>Parties</span>
            </Link>
          </Tooltip>
          <Tooltip text="Manage products and variants" position="right">
            <Link to="/products" className={isActive('/products') ? 'active' : ''} onClick={handleNavClick}>
              <HiCube className="nav-icon" />
              <span>Products</span>
            </Link>
          </Tooltip>
          {isMainBranch && (
            <Tooltip text="Manage work orders for tailoring (main branch only)" position="right">
              <Link to="/work-orders" className={isActive('/work-orders') ? 'active' : ''} onClick={handleNavClick}>
                <HiClipboardList className="nav-icon" />
                <span>Work Orders</span>
              </Link>
            </Tooltip>
          )}
          <Tooltip text="View purchases (filter by date)" position="right">
            <Link to="/purchases" className={isActive('/purchases') ? 'active' : ''} onClick={handleNavClick}>
              <HiShoppingCart className="nav-icon" />
              <span>Purchases</span>
            </Link>
          </Tooltip>
          {(user?.role === 'admin' || user?.role === 'super_admin') && isMainBranch && (
            <Tooltip text="Transfer stock from main branch to other branches" position="right">
              <Link to="/stock-transfer" className={isActive('/stock-transfer') ? 'active' : ''} onClick={handleNavClick}>
                <HiSwitchHorizontal className="nav-icon" />
                <span>Stock Transfer</span>
              </Link>
            </Tooltip>
          )}
        </div>

        {/* Sales Section */}
        <div className="sidebar-section">
          <div className="section-title">Sales</div>
          <Tooltip text="Manage sales and invoices" position="right">
            <Link to="/sales" className={isActive('/sales') ? 'active' : ''} onClick={handleNavClick}>
              <HiCurrencyDollar className="nav-icon" />
              <span>Sales</span>
            </Link>
          </Tooltip>
          <Tooltip text="Point of Sale – sell from inventory and print invoice" position="right">
            <Link to="/pos" className={isActive('/pos') ? 'active' : ''} onClick={handleNavClick}>
              <HiCash className="nav-icon" />
              <span>Point of Sale</span>
            </Link>
          </Tooltip>
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'inventory_manager' || user?.role === 'accounts_manager') && (
            <Tooltip text="Monthly profit & loss by branch" position="right">
              <Link to="/profit-loss" className={isActive('/profit-loss') ? 'active' : ''} onClick={handleNavClick}>
                <HiDocumentText className="nav-icon" />
                <span>Profit & Loss</span>
              </Link>
            </Tooltip>
          )}
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'inventory_manager' || user?.role === 'accounts_manager') && (
            <Tooltip text="Daily in/out reconciliation with PDF download" position="right">
              <Link to="/daily-inout-report" className={isActive('/daily-inout-report') ? 'active' : ''} onClick={handleNavClick}>
                <HiDocumentText className="nav-icon" />
                <span>Daily In/Out</span>
              </Link>
            </Tooltip>
          )}
          <Tooltip text="Cash & bank accounts, payments, transfers" position="right">
            <Link to="/accounts" className={isActive('/accounts') ? 'active' : ''} onClick={handleNavClick}>
              <HiCreditCard className="nav-icon" />
              <span>Cash & Bank</span>
            </Link>
          </Tooltip>
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'inventory_manager' || user?.role === 'accounts_manager') && (
            <Tooltip text="Monthly salary overview (employees only)" position="right">
              <Link to="/salary" className={isActive('/salary') ? 'active' : ''} onClick={handleNavClick}>
                <HiCash className="nav-icon" />
                <span>Salary</span>
              </Link>
            </Tooltip>
          )}
        </div>

        {/* Deleted Data Section - Collapsible */}
        <div className="sidebar-section">
          <div 
            className={`sidebar-dropdown-header ${deletedDataOpen ? 'open' : ''} ${isDeletedDataActive ? 'active' : ''}`}
            onClick={() => setDeletedDataOpen(!deletedDataOpen)}
          >
            <HiTrash className="nav-icon" />
            <span>Deleted Data</span>
            {deletedDataOpen ? (
              <HiChevronDown className="dropdown-chevron" />
            ) : (
              <HiChevronRight className="dropdown-chevron" />
            )}
          </div>
          {deletedDataOpen && (
            <div className="sidebar-dropdown-content">
              <Tooltip text="View deleted parties" position="right">
                <Link to="/deleted/parties" className={isActive('/deleted/parties') ? 'active sublink' : 'sublink'} onClick={handleNavClick}>
                  <span>Parties</span>
                </Link>
              </Tooltip>
              <Tooltip text="View deleted products and variants" position="right">
                <Link to="/deleted/products" className={isActive('/deleted/products') ? 'active sublink' : 'sublink'} onClick={handleNavClick}>
                  <span>Products</span>
                </Link>
              </Tooltip>
              <Tooltip text="View deleted work orders" position="right">
                <Link to="/deleted/work-orders" className={isActive('/deleted/work-orders') ? 'active sublink' : 'sublink'} onClick={handleNavClick}>
                  <span>Work Orders</span>
                </Link>
              </Tooltip>
            </div>
          )}
        </div>

        {user?.role === 'super_admin' && (
          <div className="sidebar-section">
            <div className="section-title">Super Admin</div>
            <Tooltip text="Manage all businesses" position="right">
              <Link to="/businesses" className={isActive('/businesses') ? 'active' : ''} onClick={handleNavClick}>
                <HiOfficeBuilding className="nav-icon" />
                <span>Businesses</span>
              </Link>
            </Tooltip>
          </div>
        )}
      </nav>
      <div className="sidebar-footer">
        © Hanzla Malik - 2026
      </div>
    </div>
  );
};

export default Sidebar;
